import * as React from 'react'

import { motion } from 'motion/react'

import { Flex, SimpleSpinner, VisuallyHidden } from '@buffer-mono/popcorn'

import { CommentsError } from '~publish/components/ChannelError/CommentsError'
import type { Channel } from '~publish/gql/graphql'
import { range } from '~publish/helpers/range'

import { CommentWrapperSkeleton, CommentWrapper } from '../CommentCard'
import type { BaseCommentsFilters } from '../CommentsFilters/types'
import type { CommentStatusWithAll } from '../types'
import {
  useCommentsData,
  useEmptyStateRenderer,
  useCommentAutoExpansion,
} from './hooks'

import styles from './CommentsList.module.css'

export const CommentListSkeleton = (): JSX.Element => (
  <Flex
    as="ol"
    role="feed"
    direction="column"
    aria-busy
    tabIndex={0}
    aria-label="Comments list"
    className={styles.feed}
  >
    <VisuallyHidden>Loading comments</VisuallyHidden>
    {range(20).map((index) => (
      <li key={index}>
        <CommentWrapperSkeleton />
      </li>
    ))}
  </Flex>
)

type CommentsListProps = {
  channel: Channel
  filters?: BaseCommentsFilters
  hasActiveFilters: boolean
  onClearFilters: () => void
}

export const CommentsList = React.memo(function CommentsList({
  channel,
  filters,
  hasActiveFilters,
  onClearFilters,
}: CommentsListProps): JSX.Element {
  const { query, comments } = useCommentsData({
    channel,
    filters,
  })

  const { loading, error, fetchingMore, lastElementRef } = query
  const status: CommentStatusWithAll | undefined = filters?.status

  // Handle comment auto-expansion for unanswered filter
  const { expandedCommentId, expandNextComment, setExpandedCommentId } =
    useCommentAutoExpansion({
      comments: comments.list,
      filters,
      channelId: channel.id,
    })

  // Determine which empty state to show (if any)
  const { EmptyState } = useEmptyStateRenderer({
    channelId: channel.id,
    hasComments: !!query.data && comments.hasComments,
    hasActiveFilters,
    status,
    onClearFilters,
  })

  if (loading && !fetchingMore) return <CommentListSkeleton />

  if (error) return <CommentsError errorMessage={error.message} />

  // Show appropriate empty state
  if (EmptyState) {
    return EmptyState
  }

  return (
    <motion.ol
      role="feed"
      aria-busy={fetchingMore}
      className={styles.feed}
      tabIndex={0}
      aria-label="Comments list"
      layout="position"
    >
      {comments.list.map((comment, index) => {
        // Comment should be expanded if it matches the expandedCommentId
        const isExpanded = comment.id === expandedCommentId

        return (
          <CommentWrapper
            key={comment.id}
            ref={
              index === comments.list.length - 1 ? lastElementRef : undefined
            }
            comment={comment}
            channel={channel}
            currentStatusFilter={filters?.status}
            isFirstRepliableComment={isExpanded}
            onExpandNextComment={expandNextComment}
            onManualExpansion={setExpandedCommentId}
          />
        )
      })}
      {fetchingMore && (
        <li className={styles.loading}>
          <SimpleSpinner size="small" />
        </li>
      )}
    </motion.ol>
  )
})
